import {
  HNPaymentCardConnection,
  HNUSBusinessAuthorizedPerson,
  HNUSBusinessProfile,
  MerchantSpendRule,
} from 'API';

// Custom type for our query with statement data
export type HnGetBusinessAccountHolderQuery = {
  hnGetBusinessAccountHolder?: {
    __typename: 'HNUSBusinessAccountHolder';
    id: string;
    financialAccounts?: {
      edges?: Array<{
        node?: {
          __typename: 'HNFinancialAccount';
          id: string;
          accountStatus?: string;
          directDepositDetails?: {
            id: string;
          } | null;
          activeBillingCycleConfiguration?: {
            paymentDueDayOfMonth: number;
            billingCycleStartDayOfMonth: number;
            billingCycleEndDayOfMonth: number;
            from: string;
            through: string | null;
          } | null;
          statementSnapshot?: {
            asOf?: string;
            currentAmountDue?: {
              value: number;
              currencyCode: string;
            } | null;
            latestClosedStatement?: {
              id: string;
              periodStart: string;
              periodEnd: string;
              openedAt: string;
              closedAt: string;
              paymentDueOn?: string | null;
              pastDueAmount?: {
                value: number;
                currencyCode: string;
              } | null;
              endingPrimaryCreditBalance?: {
                value: number;
                currencyCode: string;
              } | null;
              startingPrimaryCreditBalance?: {
                value: number;
                currencyCode: string;
              } | null;
            } | null;
            currentOpenStatement?: {
              id: string;
              periodStart: string;
              periodEnd: string;
              openedAt: string;
              closedAt: string | null;
              paymentDueOn?: string | null;
              endingPrimaryCreditBalance?: {
                value: number;
                currencyCode: string;
              } | null;
              startingPrimaryCreditBalance?: {
                value: number;
                currencyCode: string;
              } | null;
            } | null;
            updatedAt: string;
          } | null;
          statements?: {
            edges?: Array<{
              node?: {
                id: string;
                periodStart: string;
                periodEnd: string;
                openedAt: string;
                closedAt: string | null;
                endingPrimaryCreditBalance?: {
                  value: number;
                  currencyCode: string;
                } | null;
                startingPrimaryCreditBalance?: {
                  value: number;
                  currencyCode: string;
                } | null;
              } | null;
            } | null> | null;
          } | null;
          ledgers?: Array<{
            name: string;
            normalBalance: string;
            debitBalance?: {
              value: number;
              currencyCode: string;
            } | null;
            creditBalance?: {
              value: number;
              currencyCode: string;
            } | null;
          } | null> | null;
        } | null;
      } | null> | null;
    } | null;
    externalFinancialAccounts?: {
      edges?: Array<{
        node?: {
          __typename: 'HNExternalFinancialBankAccount';
          id: string;
          name?: string | null;
          accountStatus?: string;
          externalBankAccountDetails?: {
            __typename: 'HNExternalBankAccountDetail';
            routingNumber?: string | null;
            last4?: string | null;
            id: string;
            type?: string | null;
          } | null;
        } | null;
      } | null> | null;
    } | null;
  } | null;
};

export type HnGetPaymentCardsForBusinessIdQuery = {
  hnGetPaymentCardsForBusinessId?: {
    __typename: 'HNUSBusinessAccountHolder';
    id: string;
    primaryAuthorizedPerson?: HNUSBusinessAuthorizedPerson;
    businessProfile?: HNUSBusinessProfile;
    paymentCards?: HNPaymentCardConnection;
    externalId?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
};

export const hnGetPaymentCardsForBusinessId = /* GraphQL */ `
  query hnGetPaymentCardsForBusinessId(
    $id: ID!
    $first: Int = 20
    $after: String
    $filterBy: HNAccountHolderPaymentCardsFilterInput
  ) {
    hnGetPaymentCardsForBusinessId(id: $id, first: $first, after: $after, filterBy: $filterBy) {
      paymentCards {
        edges {
          cursor
          node {
            id
            last4
            network
            status
            expirationDate
            cardProduct {
              name
            }
            formFactor
            attachedSpendRules {
              edges {
                cursor
                node {
                  __typename
                  id
                  name
                  ... on HNAmountLimitSpendRule {
                    maximumAmount {
                      currencyCode
                      value
                    }
                  }
                  ... on HNMerchantCategorySpendRule {
                    merchantCategoryAllowed: allowed
                    merchantCategoryBlocked: blocked
                  }
                  ... on HNMerchantCountrySpendRule {
                    merchantCountryAllowed: allowed
                    merchantCountryBlocked: blocked
                  }
                  ... on HNCVVSpendRule {
                    CVVAllowed: allowed
                    CVVBlocked: blocked
                  }
                  ... on HNStreetAddressSpendRule {
                    streetAddressAllowed: allowed
                    streetAddressBlocked: blocked
                  }
                }
              }
            }
            attachedVelocityRules {
              edges {
                cursor
                node {
                  ... on HNVelocityRule {
                    id
                    cumulativeRule {
                      __typename
                      id
                      name
                      ... on HNAmountLimitSpendRule {
                        maximumAmount {
                          currencyCode
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
  }
`;

export const hnGetAccountHolderCardProductApplication = /* GraphQL */ `
  query HnGetAccountHolderCardProductApplication($id: ID!, $firstApplicationHistory: Int = 20) {
    hnGetAccountHolderCardProductApplication(id: $id) {
      __typename
      id
      createdAt
      updatedAt
      applicationState {
        status
      }
      cardProduct {
        name
      }
      adverseActionReasons {
        code
        description
      }
      accountHolderSnapshot {
        ... on HNUSPersonAccountHolderSnapshot {
          __typename
          name {
            givenName
            familyName
            title
            suffix
          }
          currentVerification {
            status
            reason
            results {
              code
              description
            }
            requiredDocuments {
              __typename
              createdAt
              referenceIdentifier
              updatedAt
              status
              documentUploadSession {
                ... on HNUSAccountHolderApplicationDocumentUploadSession {
                  status
                  id
                  primaryDocumentTypes
                  secondaryDocumentTypes
                  uploadRequirements {
                    constraints {
                      ... on HNDocumentUploadRequirementConstraint {
                        numberOfDocuments
                        documentType
                      }
                    }
                  }
                }
              }
              uploadedDocuments {
                ... on HNAccountHolderApplicationDocument {
                  status
                  type
                  createdAt
                  updatedAt
                }
              }
            }
          }
        }
        ... on HNUSBusinessAccountHolderSnapshot {
          __typename
          primaryAuthorizedPerson {
            name {
              givenName
              familyName
              title
              suffix
            }
            currentVerification {
              status
              reason
              results {
                code
                description
              }
              requiredDocuments {
                __typename
                createdAt
                referenceIdentifier
                updatedAt
                status
                documentUploadSession {
                  ... on HNUSAccountHolderApplicationDocumentUploadSession {
                    status
                    id
                    primaryDocumentTypes
                    secondaryDocumentTypes
                    uploadRequirements {
                      constraints {
                        ... on HNDocumentUploadRequirementConstraint {
                          numberOfDocuments
                          documentType
                        }
                      }
                    }
                  }
                }
                uploadedDocuments {
                  ... on HNAccountHolderApplicationDocument {
                    status
                    type
                    createdAt
                    updatedAt
                  }
                }
              }
            }
          }
          businessProfile {
            name {
              doingBusinessAsName
              legalBusinessName
            }
            currentCreditUnderwritingVerification {
              status
              decision {
                ... on HNUnderwriterPositiveDecision {
                  creditLine {
                    value
                    currencyCode
                  }
                }
                ... on HNUnderwriterAdverseDecision {
                  adverseActionReasons {
                    code
                    description
                  }
                }
              }
            }
            currentVerification {
              status
              reason
              results {
                code
                description
              }
              requiredDocuments {
                __typename
                createdAt
                referenceIdentifier
                updatedAt
                status
                documentUploadSession {
                  ... on HNUSAccountHolderApplicationDocumentUploadSession {
                    status
                    id
                    primaryDocumentTypes
                    secondaryDocumentTypes
                    uploadRequirements {
                      constraints {
                        ... on HNDocumentUploadRequirementConstraint {
                          numberOfDocuments
                          documentType
                        }
                      }
                    }
                  }
                }
                uploadedDocuments {
                  ... on HNAccountHolderApplicationDocument {
                    status
                    type
                    createdAt
                    updatedAt
                  }
                }
              }
            }
            ultimateBeneficialOwners {
              name {
                givenName
                familyName
                title
                suffix
              }
              currentVerification {
                status
                reason
                results {
                  code
                  description
                }
                requiredDocuments {
                  __typename
                  createdAt
                  referenceIdentifier
                  updatedAt
                  status
                  documentUploadSession {
                    ... on HNUSAccountHolderApplicationDocumentUploadSession {
                      status
                      id
                      primaryDocumentTypes
                      secondaryDocumentTypes
                      uploadRequirements {
                        constraints {
                          ... on HNDocumentUploadRequirementConstraint {
                            numberOfDocuments
                            documentType
                          }
                        }
                      }
                    }
                  }
                  uploadedDocuments {
                    ... on HNAccountHolderApplicationDocument {
                      status
                      type
                      createdAt
                      updatedAt
                    }
                  }
                }
              }
            }
          }
        }
      }
      applicationHistory(first: $firstApplicationHistory) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            ... on HNAccountHolderCardProductApplicationSnapshot {
              __typename
              createdAt
              updatedAt
              applicationState {
                status
              }
            }
          }
        }
      }
    }
  }
`;

export const hnGetFinancialAccountWithStatements = /* GraphQL */ `
  query hnGetBusinessAccountHolder($id: String!) {
    hnGetBusinessAccountHolder(id: $id) {
      id
      financialAccounts {
        edges {
          node {
            __typename
            id
            ... on HNFinancialAccount {
              id
              accountStatus
              directDepositDetails {
                id
              }
              activeBillingCycleConfiguration {
                paymentDueDayOfMonth
                billingCycleStartDayOfMonth
                billingCycleEndDayOfMonth
                from
                through
              }
              statementSnapshot {
                asOf
                currentAmountDue {
                  value
                  currencyCode
                }
                latestClosedStatement {
                  id
                  periodStart
                  periodEnd
                  openedAt
                  closedAt
                  paymentDueOn
                  pastDueAmount {
                    value
                    currencyCode
                  }
                  endingPrimaryCreditBalance {
                    value
                    currencyCode
                  }
                  startingPrimaryCreditBalance {
                    value
                    currencyCode
                  }
                }
                currentOpenStatement {
                  id
                  periodStart
                  periodEnd
                  openedAt
                  closedAt
                  paymentDueOn
                  endingPrimaryCreditBalance {
                    value
                    currencyCode
                  }
                  startingPrimaryCreditBalance {
                    value
                    currencyCode
                  }
                }
                updatedAt
              }
              statements(first: 50) {
                edges {
                  node {
                    id
                    periodStart
                    periodEnd
                    openedAt
                    closedAt
                    endingPrimaryCreditBalance {
                      value
                      currencyCode
                    }
                    startingPrimaryCreditBalance {
                      value
                      currencyCode
                    }
                    statementEntries(first: 50) {
                      edges {
                        node {
                          __typename
                          postDate
                          creditAmount {
                            value
                            currencyCode
                          }
                          debitAmount {
                            value
                            currencyCode
                          }
                          primaryCreditBalance {
                            value
                            currencyCode
                          }
                          primaryDebitBalance {
                            value
                            currencyCode
                          }
                          ledgerEntry {
                            __typename
                            id
                            memo
                            creditBalanceAtLedgerEntry {
                              value
                              currencyCode
                            }
                            debitBalanceAtLedgerEntry {
                              value
                              currencyCode
                            }
                            amount {
                              value
                              currencyCode
                            }
                          }
                        }
                        cursor
                      }
                      pageInfo {
                        hasNextPage
                        hasPreviousPage
                        startCursor
                        endCursor
                      }
                    }
                  }
                }
              }
              ledgers {
                name
                normalBalance
                debitBalance {
                  value
                  currencyCode
                }
                creditBalance {
                  value
                  currencyCode
                }
              }
            }
          }
        }
      }
      externalFinancialAccounts {
        edges {
          node {
            __typename
            ... on HNExternalFinancialBankAccount {
              id
              name
              accountStatus
              externalBankAccountDetails {
                __typename
                routingNumber
                last4
                id
                type
              }
            }
          }
        }
      }
    }
  }
`;

export const CreateHighnotePaymentCard = /* GraphQL */ `
  mutation CreateHighnotePaymentCard(
    $input: CreateHighnotePaymentCardInput!
    $condition: ModelHighnotePaymentCardConditionInput
  ) {
    createHighnotePaymentCard(input: $input, condition: $condition) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      group {
        name
      }
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            email
            user {
              id
              username
              email
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GetHighnotePaymentCard = /* GraphQL */ `
  query GetHighnotePaymentCard($paymentCardId: ID!) {
    getHighnotePaymentCard(paymentCardId: $paymentCardId) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      last4
      formFactor
      status
      hasPin
      cardholderId
      cardholder {
        id
        defaultAgaveEmployeeId
        defaultAgaveEmployee {
          id
          name
        }
      }
      group {
        id
        name
      }
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            id
            email
            user {
              id
              username
              email
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const ListHighnotePaymentCards = /* GraphQL */ `
  query ListHighnotePaymentCards($paidolId: ID!) {
    listHighnotePaymentCardsByPaidolId(paidolId: $paidolId, limit: 100) {
      nextToken
      items {
        paymentCardId
        paidolId
        authPaidolId
        cardGroupId
        name
        group {
          name
        }
        paidolUsers(limit: 100) {
          items {
            id
            paidolUser {
              email
              user {
                id
                username
                email
                first_name
                last_name
              }
            }
          }
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const UpdateHighnotePaymentCard = /* GraphQL */ `
  mutation UpdateHighnotePaymentCard(
    $input: UpdateHighnotePaymentCardInput!
    $condition: ModelHighnotePaymentCardConditionInput
  ) {
    updateHighnotePaymentCard(input: $input, condition: $condition) {
      paymentCardId
      paidolId
      authPaidolId
      group {
        id
        name
      }
      name
      hasPin
      group {
        name
      }
      paidolUsers(limit: 100) {
        items {
          paidolUser {
            email
            user {
              id
              username
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const LockUnlockPaymentCard = /* GraphQL */ `
  mutation LockUnlockPaymentCard($input: LockUnlockPaymentCardInput!) {
    lockUnlockPaymentCard(input: $input) {
      statusCode
      status
      paymentCardId
    }
  }
`;

export const SearchHighnoteTransactions = /* GraphQL */ `
  query SearchSpxTransactions($input: SearchSpxTransactionsInput!) {
    searchSpxTransactions(input: $input) {
      items {
        id
        transactionId
        userId
        user {
          id
          username
          email
          first_name
          last_name
        }
        email
        approvedAmount {
          value
          currencyCode
        }
        authPaidolId
        cardGroupId
        cardGroup {
          id
          name
        }
        createdAt
        merchantCategory
        merchantCategoryCode
        merchantName
        paidolId
        paymentCardId
        status
        note
        jobCodeId
        jobCode {
          id
          code
          description
          budget
          isActive
          jobCodeImportId
          paidolId
          authPaidolId
          createdAt
          updatedAt
        }
        procoreProjectId
        procoreProject {
          id
          name
          display_name
        }
        procoreWBSCodeId
        procoreWBSCode {
          id
          description
        }
        procoreDirectCostId
        procoreDirectCostLineItemId
        agaveCostCode {
          id
          name
          source_id
          code
          cost_type_ids
          source_data {
            data
          }
        }
        agaveCostCodeId
        agaveCostType {
          id
          name
          source_id
          code
        }
        agaveCostTypeId
        agaveProject {
          id
          name
          source_id
          number
        }
        agaveGlCodeId
        agaveGlCode {
          id
          name
          source_id
          number
          source_data {
            data
          }
        }
        agaveVendor {
          id
          name
          code
          source_id
        }
        agaveExpense {
          id
          batch
          description
          source_id
        }
        agaveExpenseId
        agaveVendorId
        agaveProjectId
        agaveEquipmentId
        agaveEquipment {
          id
          name
          code
          source_id
          emGroup
          emCo
          # SEE-1435: emCo + department are the join key for EM department
          # scoping. Omitting department leaves the scoping silently inert on
          # page load — the dropdowns fall back to the whole EM group.
          department
        }
        agaveEmCostCodeId
        agaveEmCostCode {
          id
          name
          code
          source_id
          emGroup
          emCostTypeCodes
        }
        agaveEmCostTypeId
        agaveEmCostType {
          id
          name
          code
          source_id
          emGroup
        }
        codingType
        erpBillableItemId
        erpBillableItemName
        erpBillable
        agaveSubJob {
          id
          agaveSubJobId
          name
          source_id
          code
          number
          projectId
          fullName
          cost_code_ids
        }
        agaveSubJobId
        agaveDepartmentId
        agaveDepartment {
          id
          name
          code
          source_id
          source_create_time
          source_update_time
        }
        agaveLocationId
        agaveLocation {
          id
          name
          source_id
          source_create_time
          source_update_time
        }
        agaveSubaccountId
        agaveSubaccount {
          id
          name
          code
          source_id
          source_create_time
          source_update_time
        }
        agaveEmployeeId
        agaveEmployee {
          id
          name
          source_id
          source_create_time
          source_update_time
        }
        eventDate
        accountingDate
        receiptPath
        hasReceipt
        updatedAt
        flexCodeId
        flexCode {
          id
          projectName
          projectCode
        }
        flexCostCodeId
        flexCostCode {
          id
          code
        }
        flexCostTypeId
        flexCostType {
          id
          name
        }
        flexGlCodeId
        flexGlCode {
          id
          name
        }
        flexVendorId
        flexVendor {
          id
          name
        }
        receiptPath
        hasReceipt
        flexCodeTransactionStatus
        workflowStatus
        # SEE-1604: split linkage for the parent/child rows.
        isSplitParent
        splitChildIds
        splitParentId
        splitId
        groupApprovalLevels
        owningProjectGroupIds
        returnedStatus
        approveWithoutReceipts
        approveWithoutNotes
        erpApprovalStatus
        erpBypassStatus
        erpPostingStatus
        erpPostingError
        erpPostingWarning
        sage300APInvoiceStatus
        sage300APInvoiceError
        fuelVehicleId
        fuelOdometerReading
        fuelGallons
        fuelCostPerGallon
        card {
          cardGroupId
          last4
          name
          cardholderName
          cardholderId
          group {
            id
            name
          }
        }
      }
      nextToken
      total
    }
  }
`;

export type GetMerchantSpendRulesForPaidolIdQuery = {
  listMerchantSpendRulesByPaidolId?: {
    __typename: 'ModelMerchantSpendRuleConnection';
    items: Array<MerchantSpendRule>;
    nextToken?: string | null;
  } | null;
};

export const GetMerchantSpendRulesForPaidolId = /* GraphQL */ `
  query GetMerchantSpendRulesForPaidolId($paidolId: ID!, $nextToken: String, $limit: Int = 100) {
    listMerchantSpendRulesByPaidolId(paidolId: $paidolId, nextToken: $nextToken, limit: $limit) {
      nextToken
      items {
        authPaidolId
        merchantCategorySpendRuleId
        merchantCountrySpendRuleId
        amountLimitSpendRuleId
        monthlyLimitSpendRuleId
        paidolId
        updatedAt
        name
        id
        createdAt
        paymentCards
      }
    }
  }
`;

export const GetPaymentCardDetails = /* GraphQL */ `
  query HnGetPaymentCardDetails($id: ID!, $paidolId: ID!, $yearAndMonth: String!) {
    hnGetPaymentCardDetails(id: $id) {
      id
      last4
      network
      status
      paymentCardHolderDetails {
        billingAddressOnCard {
          region
          countryCodeAlpha3
          extendedAddress
          locality
          postalCode
          streetAddress
        }
      }
      expirationDate
      cardProduct {
        name
      }
      formFactor
      attachedSpendRules {
        edges {
          cursor
          node {
            __typename
            id
            name
            ... on HNAmountLimitSpendRule {
              maximumAmount {
                currencyCode
                value
              }
            }
            ... on HNMerchantCategorySpendRule {
              allowed
              blocked
              merchantCategoryAllowed: allowed
              merchantCategoryBlocked: blocked
            }
            ... on HNMerchantCountrySpendRule {
              allowed
              blocked
              merchantCountryAllowed: allowed
              merchantCountryBlocked: blocked
            }
            ... on HNCVVSpendRule {
              CVVAllowed: allowed
              CVVBlocked: blocked
            }
            ... on HNStreetAddressSpendRule {
              streetAddressAllowed: allowed
              streetAddressBlocked: blocked
            }
          }
        }
      }
      attachedVelocityRules {
        edges {
          cursor
          node {
            ... on HNVelocityRule {
              id
              cumulativeRule {
                ... on HNAmountLimitSpendRule {
                  __typename
                  id
                  name
                  maximumAmount {
                    currencyCode
                    value
                  }
                }
              }
            }
          }
        }
      }
      velocitySpendRuleBalances {
        ... on HNVelocitySpendRuleAmountBalance {
          currentBalance {
            currencyCode
            value
          }
          remainingBalance {
            currencyCode
            value
          }
        }
      }
      physicalPaymentCardOrders {
        orderState {
          status
        }
        paymentCardShipment {
          courier {
            method
            signatureRequiredOnDelivery
            tracking {
              trackingNumber
              actualShipDateLocal
            }
          }
          requestedShipDate
        }
        createdAt
        id
      }
    }

    getHighnotePaymentCard(paymentCardId: $id) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      last4
      formFactor
      status
      hasPin
      isAdminLocked
      lastActionByRole
      lastActionByPaidolUserId
      cardholderId
      cardholder {
        id
        defaultAgaveEmployeeId
        defaultAgaveEmployee {
          id
          name
        }
      }
      group {
        id
        name
      }
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            id
            email
            user {
              id
              username
              email
              picture
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
    getBudgetSpendByMonthAndPaymentCard(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      paymentCardId: $id
    ) {
      paidolId
      authPaidolId
      yearAndMonth
      paymentCardId
      amount
      transactionCount
      createdAt
      updatedAt
    }
  }
`;

/**
 * Local database portion of GetPaymentCardDetails (excludes hnGetPaymentCardDetails which moved to CDK AppSync).
 * Used alongside highnoteService.hnGetPaymentCardDetails() to reconstruct the full response.
 */
export const GetPaymentCardLocalDetails = /* GraphQL */ `
  query GetPaymentCardLocalDetails($id: ID!, $paidolId: ID!, $yearAndMonth: String!) {
    getHighnotePaymentCard(paymentCardId: $id) {
      paymentCardId
      paidolId
      authPaidolId
      cardGroupId
      name
      last4
      formFactor
      status
      hasPin
      isAdminLocked
      lastActionByRole
      lastActionByPaidolUserId
      cardholderId
      cardholder {
        id
        defaultAgaveEmployeeId
        defaultAgaveEmployee {
          id
          name
        }
      }
      group {
        id
        name
      }
      paidolUsers(limit: 100) {
        items {
          id
          paidolUser {
            id
            email
            user {
              id
              username
              email
              picture
              first_name
              last_name
            }
          }
        }
      }
      createdAt
      updatedAt
    }
    getBudgetSpendByMonthAndPaymentCard(
      paidolId: $paidolId
      yearAndMonth: $yearAndMonth
      paymentCardId: $id
    ) {
      paidolId
      authPaidolId
      yearAndMonth
      paymentCardId
      amount
      transactionCount
      createdAt
      updatedAt
    }
  }
`;

export const GetSpendRuleById = /* GraphQL */ `
  query GetSpendRuleById($ruleId: ID!) {
    hnGetSpendRuleById(ruleId: $ruleId) {
      id
      name
      blockedCountry
      allowedCountry
      maximumAmount {
        value
      }
      allowedCategory
      blockedCategory
    }
  }
`;

export const hnViewDirectDepositDetails = /* GraphQL */ `
  query hnViewDirectDepositDetails($id: ID!) {
    hnViewDirectDepositDetails(id: $id) {
      ... on HNDirectDepositDetail {
        id
        restrictedDetails {
          ... on HNDirectDepositDetailRestrictedDetails {
            __typename
            type
            number
            routingNumber
            bank {
              name
              address {
                streetAddress
                postalCode
              }
            }
          }
          ... on HNAccessDeniedError {
            __typename
            message
          }
        }
      }
    }
  }
`;

export const GetSpendRulesConfigByPaidolId = /* GraphQL */ `
  query GetSpendRulesConfigByPaidolId(
    $paidolId: ID!
    $filter: ModelSpendRuleConfigFilterInput
    $nextToken: String
    $limit: Int = 100
  ) {
    listSpendRulesConfigByPaidolId(
      paidolId: $paidolId
      filter: $filter
      nextToken: $nextToken
      limit: $limit
    ) {
      nextToken
      items {
        id
        authPaidolId
        paidolId
        createdAt
        updatedAt
        ruleId
        ruleType
      }
    }
  }
`;

export const hnFindFinancialAccountTransfers = /* GraphQL */ `
  query HnFindFinancialAccountTransfers(
    $id: ID!
    $firstScheduledTransfers: Int = 20
    $scheduledTransfersAfterCursor: String
    $firstIntegratorTransfers: Int = 20
    $integratorTransfersAfterCursor: String
    $firstExternalTransfers: Int = 20
    $externalTransfersAfterCursor: String
  ) {
    hnFindFinancialAccountTransfers(
      id: $id
      firstScheduledTransfers: $firstScheduledTransfers
      scheduledTransfersAfterCursor: $scheduledTransfersAfterCursor
      firstIntegratorTransfers: $firstIntegratorTransfers
      integratorTransfersAfterCursor: $integratorTransfersAfterCursor
      firstExternalTransfers: $firstExternalTransfers
      externalTransfersAfterCursor: $externalTransfersAfterCursor
    ) {
      __typename
      id
      name
      incomingScheduledTransfers(first: $firstScheduledTransfers, after: $scheduledTransfersAfterCursor) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
          __typename
        }
        edges {
          cursor
          __typename
          node {
            id
            __typename
            ... on HNOneTimeACHTransfer {
              createdAt
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                  externalBankAccountDetails {
                    last4
                  }
                }
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                  externalBankAccountDetails {
                    last4
                  }
                }
              }
              scheduledTransferDate
              status
              transferAmount {
                __typename
                ... on HNManualTransferAmount {
                  amount {
                    value
                    currencyCode
                    __typename
                  }
                  __typename
                }
                ... on HNTransferAmountCalculation {
                  calculatedType
                  __typename
                }
              }
              __typename
            }
            ... on HNRecurringACHTransfer {
              createdAt
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                  externalBankAccountDetails {
                    last4
                  }
                }
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                  externalBankAccountDetails {
                    last4
                  }
                }
              }
              frequency
              nextScheduledTransferDate
              status
              transferAmount {
                __typename
                ... on HNManualTransferAmount {
                  amount {
                    value
                    currencyCode
                    __typename
                  }
                  __typename
                }
                ... on HNTransferAmountCalculation {
                  calculatedType
                  __typename
                }
              }
              __typename
            }
          }
        }
        __typename
      }
      integratorACHTransfers(first: $firstIntegratorTransfers, after: $integratorTransfersAfterCursor) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
          __typename
        }
        edges {
          node {
            __typename
            ... on HNSecureDepositACHTransfer {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              settlementDate
              status {
                ... on HNNonFailureIntegratorInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureIntegratorInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
            ... on HNSecureCardBalanceRepaymentACHTransfer {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              settlementDate
              status {
                ... on HNNonFailureIntegratorInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureIntegratorInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
            ... on HNIntegratorInitiatedFundsWithdrawalACHTransfer {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              settlementDate
              status {
                ... on HNNonFailureIntegratorInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureIntegratorInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
            ... on HNIntegratorInitiatedFundsDepositACHTransfer {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              settlementDate
              status {
                ... on HNNonFailureIntegratorInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureIntegratorInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
          }
          __typename
        }
        __typename
      }
      externalACHTransfers(first: $firstExternalTransfers, after: $externalTransfersAfterCursor) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
          __typename
        }
        edges {
          node {
            __typename
            ... on HNDebitFunds {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              fromFinancialAccount {
                id
                name
                __typename
              }
              settlementDate
              status {
                ... on HNNonFailureExternallyInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureExternallyInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
            ... on HNCreditFunds {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              toFinancialAccount {
                id
                name
                __typename
              }
              settlementDate
              status {
                ... on HNNonFailureExternallyInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureExternallyInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
            ... on HNPayrollTransfer {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              toFinancialAccount {
                id
                name
                __typename
              }
              settlementDate
              status {
                ... on HNNonFailureExternallyInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureExternallyInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
            ... on HNSecureDeposit {
              id
              createdAt
              amount {
                value
                currencyCode
                __typename
              }
              companyIdentifier
              companyName
              descriptor {
                companyEntryDescription
                individualIdentificationNumber
                individualName
              }
              toFinancialAccount {
                __typename
                ... on HNFinancialAccount {
                  id
                  name
                  __typename
                }
                ... on HNExternalFinancialBankAccount {
                  id
                  name
                  __typename
                }
                ... on HNNonVerifiedExternalUSFinancialBankAccount {
                  id
                  name
                  __typename
                }
              }
              settlementDate
              status {
                ... on HNNonFailureExternallyInitiatedACHStatus {
                  status
                  __typename
                }
                ... on HNFailureExternallyInitiatedACHStatus {
                  status
                  statusReasonCode
                  __typename
                }
                __typename
              }
              __typename
            }
          }
          __typename
        }
        __typename
      }
    }
  }
`;

export const hnFindFinancialAccountLedgerActivity = /* GraphQL */ `
  query HnFindFinancialAccountLedgerActivity(
    $id: ID!
    $firstActivities: Int = 20
    $activitiesAfterCursor: String
  ) {
    hnFindFinancialAccountLedgerActivity(
      id: $id
      firstActivities: $firstActivities
      activitiesAfterCursor: $activitiesAfterCursor
    ) {
      __typename
      id
      ledgers(ledgerNames: [CASH, OUTSTANDING_BALANCE_PAYABLE]) {
        id
        normalBalance
        asOf
        name
        creditBalance {
          value
          currencyCode
          __typename
        }
        debitBalance {
          value
          currencyCode
          __typename
        }
        ledgerEntries(first: $firstActivities, after: $activitiesAfterCursor) {
          ... on HNLedgerEntryConnection {
            pageInfo {
              startCursor
              endCursor
              hasNextPage
              hasPreviousPage
              __typename
            }
            edges {
              cursor
              __typename
              node {
                __typename
                id
                memo
                createdAt
                creditBalanceAtLedgerEntry {
                  value
                  currencyCode
                  __typename
                }
                debitBalanceAtLedgerEntry {
                  value
                  currencyCode
                  __typename
                }
                amount {
                  value
                  currencyCode
                  __typename
                }
                financialEvent {
                  __typename
                  ... on HNNode {
                    id
                    __typename
                  }
                  ... on HNAuthorizationAndClearEvent {
                    merchantDetails {
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNAuthorizationEvent {
                    merchantDetails {
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNClearingEvent {
                    merchantDetails {
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNIssuerPreliminaryAuthorizationEvent {
                    merchantDetails {
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNReversalEvent {
                    merchantDetails {
                      name
                      __typename
                    }
                    settlementImpact
                    __typename
                  }
                  ... on HNCreditFundsACHTransferEvent {
                    externallyInitiatedACHStatus: status {
                      ... on HNNonFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNDebitFundsACHTransferEvent {
                    externallyInitiatedACHStatus: status {
                      ... on HNNonFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    fromFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNSecureDepositTransferEvent {
                    externallyInitiatedACHStatus: status {
                      ... on HNNonFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNPayrollACHTransferEvent {
                    externallyInitiatedACHStatus: status {
                      ... on HNNonFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureExternallyInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNIntegratorInitiatedFundsDepositACHTransferEvent {
                    integratorInitiatedACHStatus: status {
                      ... on HNNonFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNIntegratorInitiatedFundsWithdrawalACHTransferEvent {
                    integratorInitiatedACHStatus: status {
                      ... on HNNonFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    fromFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNSecureDepositACHTransferEvent {
                    integratorInitiatedACHStatus: status {
                      ... on HNNonFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNSecureCardBalanceRepaymentACHTransferEvent {
                    integratorInitiatedACHStatus: status {
                      ... on HNNonFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      ... on HNFailureIntegratorInitiatedACHStatus {
                        status
                        __typename
                      }
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNInterFinancialAccountTransfer {
                    id
                    memo
                    purpose
                    fromFinancialAccount {
                      id
                      name
                      __typename
                    }
                    toFinancialAccount {
                      id
                      name
                      __typename
                    }
                    __typename
                  }
                  ... on HNFeeTransferEvent {
                    feeTransfer {
                      __typename
                      ... on HNChargedFeeTransfer {
                        feeActivityType
                        __typename
                      }
                      ... on HNReversedFeeTransfer {
                        sourceFeeTransferEvent {
                          feeTransfer {
                            __typename
                            ... on HNChargedFeeTransfer {
                              feeActivityType
                              __typename
                            }
                          }
                          __typename
                        }
                        __typename
                      }
                    }
                    __typename
                  }
                  ... on HNManualAdjustmentEvent {
                    adjustmentActivityType
                    __typename
                  }
                }
              }
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
  }
`;

export const hnFindFinancialAccountActivities = /* GraphQL */ `
  query HnFindFinancialAccountActivities(
    $id: ID!
    $firstActivities: Int = 20
    $filterBy: HNFinancialAccountActivitiesFilterInput
    $activitiesAfterCursor: String
  ) {
    hnFindFinancialAccountActivities(
      id: $id
      firstActivities: $firstActivities
      filterBy: $filterBy
      activitiesAfterCursor: $activitiesAfterCursor
    ) {
      __typename
      id
      financialAccountActivities(
        first: $firstActivities
        filterBy: $filterBy
        after: $activitiesAfterCursor
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
          __typename
        }
        edges {
          cursor
          node {
            __typename
            sign
            isComplete
            pendingAmount {
              value
              currencyCode
              __typename
            }
            postedAmount {
              value
              currencyCode
              __typename
            }
            createdAt
            source {
              __typename
              ... on HNDebitTransaction {
                __typename
                id
                transactionEvents {
                  __typename
                  ... on HNNode {
                    id
                    __typename
                  }
                  ... on HNAuthorizationEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNIssuerPreliminaryAuthorizationEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNClearingEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    disputes {
                      id
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNBalanceInquiryEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNVerificationEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNAuthorizationAndClearEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    disputes {
                      id
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNReversalEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    settlementImpact
                    __typename
                  }
                }
              }
              ... on HNCreditTransaction {
                __typename
                id
                transactionEvents {
                  __typename
                  ... on HNNode {
                    id
                    __typename
                  }
                  ... on HNAuthorizationEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNIssuerPreliminaryAuthorizationEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNClearingEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    disputes {
                      id
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNBalanceInquiryEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNVerificationEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNAuthorizationAndClearEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    disputes {
                      id
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    __typename
                  }
                  ... on HNReversalEvent {
                    paymentCard {
                      id
                      last4
                      __typename
                    }
                    merchantDetails {
                      category
                      name
                      __typename
                    }
                    pointOfServiceDetails {
                      isCardPresent
                      __typename
                    }
                    settlementImpact
                    __typename
                  }
                }
              }
              ... on HNFeeTransferEvent {
                id
                feeTransfer {
                  __typename
                  ... on HNChargedFeeTransfer {
                    cardProduct {
                      id
                      name
                      __typename
                    }
                    feeActivityType
                    feeAmount {
                      value
                      currencyCode
                      __typename
                    }
                    feeChargeActvity {
                      __typename
                      ... on HNPhysicalPaymentCardOrder {
                        paymentCard {
                          id
                          __typename
                        }
                        __typename
                      }
                      ... on HNPaymentCard {
                        id
                        __typename
                      }
                      ... on HNFinancialAccount {
                        id
                        __typename
                      }
                      ... on HNSecureDepositACHTransfer {
                        id
                        __typename
                      }
                      ... on HNSecureCardBalanceRepaymentACHTransfer {
                        id
                        __typename
                      }
                      ... on HNIntegratorInitiatedFundsWithdrawalACHTransfer {
                        id
                        __typename
                      }
                      ... on HNIntegratorInitiatedFundsDepositACHTransfer {
                        id
                        __typename
                      }
                      ... on HNAuthorizationAndClearEvent {
                        id
                        pointOfServiceDetails {
                          isCardPresent
                          __typename
                        }
                        __typename
                      }
                      ... on HNClearingEvent {
                        id
                        pointOfServiceDetails {
                          isCardPresent
                          __typename
                        }
                        __typename
                      }
                      ... on HNBalanceInquiryEvent {
                        id
                        pointOfServiceDetails {
                          isCardPresent
                          __typename
                        }
                        __typename
                      }
                    }
                    feeChargeRule {
                      name
                      __typename
                    }
                    originatingAccount {
                      id
                      name
                      __typename
                    }
                    receivingAccount {
                      id
                      name
                      __typename
                    }
                    status {
                      __typename
                      ... on HNNonFailureFeeTransferStatus {
                        status
                        __typename
                      }
                      ... on HNFailureFeeTransferStatus {
                        status
                        reasonDetails {
                          reason
                          type
                          __typename
                        }
                        __typename
                      }
                    }
                    transferDate
                    transferedFeeAmount {
                      value
                      currencyCode
                      __typename
                    }
                    __typename
                  }
                  ... on HNReversedFeeTransfer {
                    originatingAccount {
                      id
                      name
                      __typename
                    }
                    receivingAccount {
                      id
                      name
                      __typename
                    }
                    sourceFeeTransferEvent {
                      id
                      feeTransfer {
                        __typename
                        ... on HNChargedFeeTransfer {
                          feeActivityType
                          feeChargeActvity {
                            __typename
                            ... on HNPhysicalPaymentCardOrder {
                              paymentCard {
                                id
                                __typename
                              }
                              __typename
                            }
                            ... on HNPaymentCard {
                              id
                              __typename
                            }
                            ... on HNFinancialAccount {
                              id
                              __typename
                            }
                            ... on HNSecureDepositACHTransfer {
                              id
                              __typename
                            }
                            ... on HNSecureCardBalanceRepaymentACHTransfer {
                              id
                              __typename
                            }
                            ... on HNIntegratorInitiatedFundsWithdrawalACHTransfer {
                              id
                              __typename
                            }
                            ... on HNIntegratorInitiatedFundsDepositACHTransfer {
                              id
                              __typename
                            }
                            ... on HNAuthorizationAndClearEvent {
                              id
                              pointOfServiceDetails {
                                isCardPresent
                                __typename
                              }
                              __typename
                            }
                            ... on HNClearingEvent {
                              id
                              pointOfServiceDetails {
                                isCardPresent
                                __typename
                              }
                              __typename
                            }
                            ... on HNBalanceInquiryEvent {
                              id
                              pointOfServiceDetails {
                                isCardPresent
                                __typename
                              }
                              __typename
                            }
                          }
                          __typename
                        }
                      }
                      __typename
                    }
                    status {
                      __typename
                      ... on HNNonFailureFeeTransferStatus {
                        status
                        __typename
                      }
                      ... on HNFailureFeeTransferStatus {
                        status
                        reasonDetails {
                          reason
                          type
                          __typename
                        }
                        __typename
                      }
                    }
                    transferDate
                    transferedFeeAmount {
                      value
                      currencyCode
                      __typename
                    }
                    __typename
                  }
                }
                __typename
              }
              ... on HNInterFinancialAccountTransfer {
                id
                memo
                purpose
                fromFinancialAccount {
                  id
                  name
                  __typename
                }
                toFinancialAccount {
                  id
                  name
                  __typename
                }
                __typename
              }
              ... on HNCreditFunds {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                toFinancialAccount {
                  id
                  name
                  __typename
                }
                createdAt
                settlementDate
                __typename
              }
              ... on HNDebitFunds {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                fromFinancialAccount {
                  id
                  name
                  __typename
                }
                createdAt
                settlementDate
                __typename
              }
              ... on HNSecureDeposit {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                toFinancialAccount {
                  __typename
                  ... on HNFinancialAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNExternalFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNNonVerifiedExternalUSFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                }
                createdAt
                settlementDate
                __typename
              }
              ... on HNPayrollTransfer {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                toFinancialAccount {
                  id
                  name
                  __typename
                }
                createdAt
                settlementDate
                __typename
              }
              ... on HNSecureDepositACHTransfer {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                createdAt
                settlementDate
                toFinancialAccount {
                  __typename
                  ... on HNFinancialAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNExternalFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNNonVerifiedExternalUSFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                }
                __typename
              }
              ... on HNSecureCardBalanceRepaymentACHTransfer {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                createdAt
                settlementDate
                toFinancialAccount {
                  __typename
                  ... on HNFinancialAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNExternalFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNNonVerifiedExternalUSFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                }
                __typename
              }
              ... on HNIntegratorInitiatedFundsWithdrawalACHTransfer {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                createdAt
                settlementDate
                fromFinancialAccount {
                  __typename
                  ... on HNFinancialAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNExternalFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNNonVerifiedExternalUSFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                }
                __typename
              }
              ... on HNIntegratorInitiatedFundsDepositACHTransfer {
                id
                amount {
                  value
                  currencyCode
                  __typename
                }
                companyIdentifier
                companyName
                descriptor {
                  companyEntryDescription
                  individualName
                  __typename
                }
                createdAt
                settlementDate
                toFinancialAccount {
                  __typename
                  ... on HNFinancialAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNExternalFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                  ... on HNNonVerifiedExternalUSFinancialBankAccount {
                    id
                    name
                    __typename
                  }
                }
                __typename
              }
            }
          }
          __typename
        }
        __typename
      }
    }
  }
`;

export const getCards = /* GraphQL */ `
  query GetCards($input: GetCardsInput!) {
    getCards(input: $input) {
      nextToken
      total
      items {
        paymentCardId
        paidolId
        formFactor
        name
        last4
        expirationDate
        status
        procoreEmployee {
          id
          name
          email_address
        }
        cardholder {
          id
          name
          email
          picture
        }
        cardGroup {
          id
          name
        }
        spxSpendRule {
          id
          name
        }
        monthlySpendLimit {
          id
          name
          amount
          currency
          cumulativeRuleId
        }
        maximumTransactionAmount {
          id
          amount
          currency
        }
        spent {
          value
          currencyCode
        }
        isAdminLocked
        isAuthorizedUserCard
      }
    }
  }
`;

export const getCardsLite = /* GraphQL */ `
  query GetCardsLite($input: GetCardsInput!) {
    getCardsLite(input: $input) {
      nextToken
      total
      items {
        paymentCardId
        last4
        name
        cardholder {
          name
          picture
        }
        monthlySpendLimit {
          name
          amount
        }
      }
    }
  }
`;

export const hnGetBusinessAccountHolderDetails = /* GraphQL */ `
  query hnGetBusinessAccountHolderDetails($id: String!) {
    hnGetBusinessAccountHolder(id: $id) {
      ... on HNUSBusinessAccountHolder {
        id
        businessProfile {
          id
          businessType
          name {
            doingBusinessAsName
            legalBusinessName
          }
          billingAddress {
            streetAddress
            extendedAddress
            postalCode
            region
            locality
            countryCodeAlpha3
          }
        }
        primaryAuthorizedPerson {
          id
          email
          authorizingPersonTitle
          dateOfBirth
          percentageOwnership
          name {
            givenName
            familyName
          }
          homeAddress {
            streetAddress
            extendedAddress
            postalCode
            region
            locality
            countryCodeAlpha3
          }
        }
      }
    }
  }
`;

export const hnGetStatementTransactions = /* GraphQL */ `
  query HnGetStatementTransactions($id: ID!, $first: Int = 50) {
    hnGetStatementTransactions(id: $id, first: $first) {
      id
      periodStart
      periodEnd
      openedAt
      closedAt
      paymentDueOn
      pastDueAmount {
        value
        currencyCode
      }
      periodMinimumPaymentDue {
        value
        currencyCode
      }
      primaryLedger {
        id
        name
        normalBalance
        creditBalance {
          value
          currencyCode
        }
        debitBalance {
          value
          currencyCode
        }
      }
      startingPrimaryCreditBalance {
        value
        currencyCode
      }
      startingPrimaryDebitBalance {
        value
        currencyCode
      }
      endingPrimaryCreditBalance {
        value
        currencyCode
      }
      endingPrimaryDebitBalance {
        value
        currencyCode
      }
      periodInterestCharges {
        value
        currencyCode
      }
      statementEntries {
        edges {
          node {
            postDate
            creditAmount {
              value
              currencyCode
            }
            debitAmount {
              value
              currencyCode
            }
            primaryCreditBalance {
              value
              currencyCode
            }
            primaryDebitBalance {
              value
              currencyCode
            }
            ledgerEntry {
              id
              memo
              creditBalanceAtLedgerEntry {
                value
                currencyCode
              }
              debitBalanceAtLedgerEntry {
                value
                currencyCode
              }
              amount {
                value
                currencyCode
              }
            }
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;

// Highnote simulation helpers for statements (used in onboarding/dev tools)
export const hnSimulateShiftCurrentFinancialAccountStatement = /* GraphQL */ `
  mutation hnSimulateShiftCurrentFinancialAccountStatement(
    $input: HNSimulateShiftCurrentFinancialAccountStatementPeriodInput!
  ) {
    hnSimulateShiftCurrentFinancialAccountStatement(input: $input) {
      __typename
    }
  }
`;

export const hnSimulateRolloverCurrentFinancialAccountStatement = /* GraphQL */ `
  mutation hnSimulateRolloverCurrentFinancialAccountStatement(
    $input: HNSimulateRolloverCurrentFinancialAccountStatementPeriodInput!
  ) {
    hnSimulateRolloverCurrentFinancialAccountStatement(input: $input) {
      __typename
    }
  }
`;

export const getAgaveAccount = /* GraphQL */ `
  query GetAgaveAccount($input: AgaveAccountInput!) {
    getAgaveAccount(input: $input) {
      account_id
      connection {
        type
        __typename
      }
      created_at
      linked_company {
        id
        source_id
        name
        __typename
      }
      linked_user {
        id
        source_id
        name
        email
        __typename
      }
      reference_id
      source_system {
        name
        slug
        __typename
      }
      source_system_environment {
        name
        slug
        __typename
      }
      status
      supports_cross_account_uuid
      supports_cross_company_requests
      __typename
    }
  }
`;

export const getAgaveEmployees = /* GraphQL */ `
  query GetAgaveEmployees($input: AgaveEmployeesInput!) {
    getAgaveEmployees(input: $input) {
      id
      source_id
      address {
        city
        country
        postal_code
        state
        street_1
        street_2
        type
        __typename
      }
      company
      email
      first_name
      full_name
      gender
      hire_date
      industry
      job_title
      last_name
      mobile_phone
      phone
      source_create_time
      source_update_time
      status
      termination_date
      type
      source_data {
        path
        content_type
        __typename
      }
      __typename
    }
  }
`;

export const getAgaveBankAccounts = /* GraphQL */ `
  query GetAgaveBankAccounts($input: AgaveBankAccountsInput!) {
    getAgaveBankAccounts(input: $input) {
      id
      source_id
      bank_name
      currency_code
      current_balance
      description
      ledger_account_id
      name
      number
      source_create_time
      source_update_time
      status
      type
      source_data {
        path
        content_type
        data
        __typename
      }
      __typename
    }
  }
`;

export const getAgaveVendorByName = /* GraphQL */ `
  query GetAgaveVendorByName($input: GetAgaveVendorInput!) {
    getAgaveVendorByName(input: $input) {
      id
      paidolId
      authPaidolId
      source_id
      address {
        street_1
        street_2
        city
        state
        country
        postal_code
        __typename
      }
      code
      email
      fax
      name
      phone
      status
      tax_number
      terms
      type
      website
      source_create_time
      source_update_time
      source_data {
        V1099YN
        V1099Type
        V1099Box
        EFT
        data
        __typename
      }
      createdAt
      updatedAt
      fullName
      __typename
    }
  }
`;

export const getAgaveVendors = /* GraphQL */ `
  query GetAgaveVendors($input: GetAgaveVendorsInput!) {
    getAgaveVendors(input: $input) {
      id
      paidolId
      authPaidolId
      source_id
      address {
        street_1
        street_2
        city
        state
        country
        postal_code
        __typename
      }
      code
      email
      fax
      name
      phone
      status
      tax_number
      terms
      type
      website
      source_create_time
      source_update_time
      source_data {
        V1099YN
        V1099Type
        V1099Box
        EFT
        data
        __typename
      }
      createdAt
      updatedAt
      fullName
      __typename
    }
  }
`;

export const getAgaveSyncByPaidolId = /* GraphQL */ `
  query GetAgaveSyncByPaidolId($input: GetAgaveSyncByPaidolIdInput!) {
    getAgaveSyncByPaidolId(input: $input) {
      sync {
        id
        paidolId
        authPaidolId
        inProgress
        error
        lastRunStart
        lastRunEnd
        taskArn
        syncEntityFilter
        createdAt
        updatedAt
        __typename
      }
      __typename
    }
  }
`;

export const getPaidolAgaveToken = /* GraphQL */ `
  query GetPaidolAgaveToken($id: ID!) {
    getPaidol(id: $id) {
      id
      agaveAccountToken
      __typename
    }
  }
`;

export const getAgaveAPInvoiceByFilterSageIntacct = /* GraphQL */ `
  query GetAgaveAPInvoiceByFilterSageIntacct($input: GetAgaveAPInvoiceByFilterSageIntacctInput!) {
    getAgaveAPInvoiceByFilterSageIntacct(input: $input) {
      id
      source_id
      amount
      amount_due
      ap_payment_ids
      attachments {
        id
        source_id
        name
        url
        source_create_time
        source_update_time
        __typename
      }
      batch
      batch_id
      contract {
        id
        type
        __typename
      }
      creator_id
      currency_code
      description
      discount_amount
      discount_date
      due_date
      issue_date
      line_items {
        id
        source_id
        amount
        cost_code_id
        cost_type_id
        customer_id
        description
        discount_amount
        item_id
        ledger_account_id
        project_id
        quantity
        retention_amount
        status
        sub_job_id
        tax_amount
        tax_code_id
        type
        unit_cost
        unit_of_measure
        vendor_id
        wbs_id
        source_create_time
        source_update_time
        __typename
      }
      misc_amount
      net_amount
      number
      project_id
      retention_amount
      retention_percent
      status
      subtotal_amount
      tax_amount
      total_amount
      type
      vendor_id
      source_create_time
      source_update_time
      source_data {
        path
        content_type
        data
        __typename
      }
      __typename
    }
  }
`;
